import { cache } from "@/config"

/**
 * POST 方式创建 SSE 流式连接
 */
export function createCozeStream(url, body = {}, callbacks = {}) {
    const {
        onMessage = () => {},
        onError = () => {},
        onComplete = () => {},
        onOpen = () => {},
        onAuthError = () => {},  // ✅ 新增：登录过期回调
        onSessionId = () => {}     // ✅ 新增：收到 session_id 回调
    } = callbacks;

    const controller = new AbortController();
    let fullContent = '';
    let isClosed = false;
    let sessionIdCaptured = false; // ✅ 防止重复回调

    const tokenName = "Authorization-Token" //登录凭证名称
    const getToken = () => { //获取登录信息
        return typeof (cache("userInfo")[tokenName]) === 'undefined' ? '' : cache("userInfo")[tokenName]
    }

    // ✅ 安全序列化，防止循环引用
    let safeBody;
    try {
        safeBody = JSON.stringify(body);
    } catch (e) {
        console.error('请求体序列化失败:', e);
        onError(new Error('请求参数包含循环引用'));
        return { close: () => {}, getContent: () => '' };
    }

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            [tokenName]: getToken(),
        },
        credentials: 'include',
        body: safeBody,
        signal: controller.signal,
    })
    .then(async response => {
        // ✅ 关键：先检查 Content-Type，区分 JSON 错误 和 SSE 流
        const contentType = response.headers.get('Content-Type') || '';
        // 情况1：返回的是 JSON（登录过期等业务错误）
        if (contentType.includes('application/json')) {
            const jsonData = await response.json();
            
            // 登录过期
            if (jsonData.status === 401) {
                onAuthError(jsonData.message || '登录过期，请重新登录');
                isClosed = true;
                return;
            }
            
            // 其他业务错误
            // onError(new Error(jsonData.message || '请求失败'));
            onError(jsonData.message || '请求失败');
            isClosed = true;
            return;
        }
        
        // 情况2：返回的是 SSE 流（正常流程）
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        onOpen();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // 递归读取流数据
        function readStream() {
            if (isClosed) return;

            reader.read().then(({ done, value }) => {
                if (done || isClosed) {
                    if (!isClosed) {
                        isClosed = true;
                        onComplete(fullContent);
                    }
                    return;
                }

                // 解码数据
                const chunk = decoder.decode(value, { stream: true });
                // console.log(chunk)
                buffer += chunk;

                // 按 SSE 格式解析（以 \n\n 分隔）
                const parts = buffer.split('\n\n');
                buffer = parts.pop(); // 保留不完整部分

                for (const part of parts) {
                    if (!part || !part.trim()) continue;

                    // 解析 SSE 字段
                    const lines = part.split('\n');
                    let eventName = 'message';
                    const dataLines = [];

                    for (const line of lines) {
                        
                        if (!line || typeof line !== 'string') continue;
                        
                        const colonIndex = line.indexOf(':');
                        if (colonIndex === -1) continue;

                        const key = line.substring(0, colonIndex).trim();
                        const value = line.substring(colonIndex + 1).trim();

                        if (key === 'event') {
                            eventName = value;
                        } else if (key === 'data') {
                            dataLines.push(value);
                        }
                    }

                    const dataStr = dataLines.join('\n');
                    if (!dataStr) continue;

                    // ✅ 处理后端自定义的 session_id 事件
                    if (eventName === 'session_id') {
                        try {
                            const parsed = JSON.parse(dataStr);
                            if (parsed.session_id && !sessionIdCaptured) {
                                sessionIdCaptured = true;
                                onSessionId(parsed.session_id);
                            }
                        } catch (e) {
                            console.warn('session_id 事件解析失败:', dataStr);
                        }
                        continue;
                    }

                    if (dataStr === '[DONE]') {
                        console.log('[DONE]');
                        isClosed = true;
                        onComplete(fullContent);
                        return;
                    }

                    let parsed;
                    try {
                        parsed = JSON.parse(dataStr);
                    } catch (e) {
                        fullContent += dataStr;
                        onMessage(dataStr, fullContent, { raw: dataStr });
                        continue;
                    }

                    // ✅ 关键修正：从 Coze data JSON 中提取 session_id
                    if (parsed.session_id && !sessionIdCaptured) {
                        sessionIdCaptured = true;
                        onSessionId(parsed.session_id);
                    }

                    // ✅ 关键修正：检测到 message_end 时结束输出
                    if (parsed.type === 'message_end') {
                        // console.log('message_end detected:', parsed);
                        isClosed = true;
                        onComplete(fullContent);
                        return;
                    }

                    const content = extractContent(parsed);
                    if (content) {
                        fullContent += content;
                        onMessage(content, fullContent, parsed);
                    }
                }

                // 继续读取
                readStream();

            }).catch(error => {
                if (!isClosed) {
                    isClosed = true;
                    onError(error);
                }
            });
        }

        readStream();
    }).catch(error => {
        if (!isClosed) {
            isClosed = true;
            onError(error);
        }
    });

    return {
        close: () => {
            isClosed = true;
            controller.abort();
        },
        getContent: () => fullContent
    };
}

/**
 * 从 Coze 响应中提取文本内容
 * 支持 type='answer' 和 type='tool_response'
 */
function extractContent(data) {
    if (!data || typeof data !== 'object') {
        return typeof data === 'string' ? data : '';
    }
    
    // 只提取 type='answer' 的 content.answer
    if (data.type === 'answer' && data.content) {
        if (typeof data.content.answer === 'string') {
            return data.content.answer;
        }
    }
    
    // // 只提取 type='tool_response' 的 content.tool_response.result
    // if (data.type === 'tool_response' && data.content?.tool_response?.result) {
    //     console.log('tool_response', data.content.tool_response.result);
    //     return data.content.tool_response.result;
    // }
    
    return '';
}